// @ts-nocheck
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useAgentSidebar } from '@/components/layout/AgentSidebarContext'
import { deriveFromPath } from '@/features/cases/navigation'
import { useFilterStore, useFilterStoreApi } from '@/stores/filter-store-provider'
import { useSelectionStore } from '@/stores/selection-store'
import type { ActiveGraphFilter } from '@/types/graph-query'

export interface InvestigateContextValue {
  activeGraphFilters: ActiveGraphFilter[]
  addFilter: (filter: ActiveGraphFilter) => void
  removeFilter: (id: string) => void
  clearFilters: () => void
  componentFilter: { componentId: number; label: string } | null
  setComponentFilter: (f: { componentId: number; label: string } | null) => void
  graphFocusNodeId: string | null
  setGraphFocusNodeId: (id: string | null) => void
  registerGraphActionHandler: (handler: (action: any) => void) => void
  dispatchGraphAction: (action: any) => void
  setPendingGraphAction: (action: any) => void
}

const InvestigateContext = createContext<InvestigateContextValue | null>(null)

export function InvestigateContextProvider({ children }: { children: React.ReactNode }) {
  // Chip set lives in the filter store. The Investigate context exposes the
  // same `activeGraphFilters` / `addFilter` / `removeFilter` / `clearFilters`
  // surface for backward compatibility with existing consumers; mutations are
  // routed through the store so the canonical `GraphView` is recomputed and
  // shared with every Investigate view via the store.
  const activeGraphFilters = useFilterStore(state => state.chips)
  const storeApi = useFilterStoreApi()

  const [componentFilter, setComponentFilter] = useState<{
    componentId: number
    label: string
  } | null>(null)
  const [graphFocusNodeId, setGraphFocusNodeId] = useState<string | null>(null)

  const location = useLocation()
  const navigate = useNavigate()
  const { dispatchGraphAction: dispatchAgentGraphAction } = useAgentSidebar()
  const graphHandlerRef = useRef<((action: any) => void) | null>(null)
  const pendingActionRef = useRef<any | null>(null)
  const pendingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(
    () => () => {
      if (pendingTimerRef.current) clearTimeout(pendingTimerRef.current)
    },
    []
  )

  const addFilter = useCallback(
    (filter: ActiveGraphFilter) => {
      const next = [...storeApi.getState().chips, filter]
      storeApi.getState().setChipsAndFetch(next)
      if ('componentId' in filter) {
        setComponentFilter({
          componentId: (filter as any).componentId,
          label: (filter as any).label,
        })
      }
    },
    [storeApi]
  )

  const removeFilter = useCallback(
    (id: string) => {
      const next = storeApi.getState().chips.filter(f => f.id !== id)
      storeApi.getState().setChipsAndFetch(next)
      if (next.length === 0) setComponentFilter(null)
    },
    [storeApi]
  )

  const clearFilters = useCallback(() => {
    storeApi.getState().setChipsAndFetch([])
    setComponentFilter(null)
  }, [storeApi])

  // Deep links into the Investigate views: `?focus=<id>` goes to a node, and
  // `?filter_type=<t>&filter_id=<id>&filter_label=<l>` adds a filter scoped to
  // that node. Params are consumed once and stripped from the URL.
  useEffect(() => {
    const params = new URLSearchParams(location.search)
    const focus = params.get('focus')
    const filterType = params.get('filter_type')
    const filterId = params.get('filter_id')
    const filterLabel = params.get('filter_label')

    let consumed = false

    if (filterType && filterId) {
      addFilter({
        id: `agent-${Date.now()}`,
        label: filterLabel || filterId,
        origin: { entityType: filterType, elementId: filterId },
        chain: [],
      } as ActiveGraphFilter)
      params.delete('filter_type')
      params.delete('filter_id')
      params.delete('filter_label')
      consumed = true
    }

    if (focus) {
      // One request, by the channel the view on screen reads. The Explore
      // graph centres the node from this context, once it has loaded it. The
      // investigation canvas and the other lens views follow the shared
      // selection, where `focusEntity` is the "take me to this node" request;
      // it is made here, once the case's views are mounted: a selection made
      // before that is taken back by `SelectionBridge` as it mounts.
      if (deriveFromPath(location.pathname).viewType === 'knowledge-graph') {
        setGraphFocusNodeId(focus)
      } else {
        useSelectionStore.getState().focusEntity(focus, 'graph')
      }
      params.delete('focus')
      consumed = true
    }

    if (consumed) {
      navigate(
        { pathname: location.pathname, search: params.toString() ? `?${params}` : '' },
        { replace: true }
      )
    }

    // Consume a pending path-highlight action stashed by the agent tool UI
    // when navigation crossed a route without an InvestigateContext handler.
    try {
      const raw = sessionStorage.getItem('spectre:pendingPath')
      if (raw) {
        sessionStorage.removeItem('spectre:pendingPath')
        const action = JSON.parse(raw)
        let attempts = 0
        const maxAttempts = 25
        const retry = setInterval(() => {
          attempts += 1
          const cy = (window as any).cytoscapeInstance
          const ready = cy && cy.nodes && cy.nodes().length > 0
          if (ready || attempts >= maxAttempts) {
            clearInterval(retry)
            dispatchAgentGraphAction(action)
          }
        }, 200)
      }
    } catch {
      /* ignore storage/JSON errors */
    }
  }, [location.search, location.pathname, navigate, addFilter, dispatchAgentGraphAction])

  const registerGraphActionHandler = useCallback((handler: (action: any) => void) => {
    graphHandlerRef.current = handler
    if (pendingActionRef.current) {
      const pending = pendingActionRef.current
      pendingActionRef.current = null
      pendingTimerRef.current = setTimeout(() => handler(pending), 1500)
    }
  }, [])

  const dispatchGraphAction = useCallback((action: any) => {
    if (graphHandlerRef.current) {
      graphHandlerRef.current(action)
    } else {
      pendingActionRef.current = action
    }
  }, [])

  const setPendingGraphAction = useCallback((action: any) => {
    pendingActionRef.current = action
  }, [])

  const value: InvestigateContextValue = {
    activeGraphFilters,
    addFilter,
    removeFilter,
    clearFilters,
    componentFilter,
    setComponentFilter,
    graphFocusNodeId,
    setGraphFocusNodeId,
    registerGraphActionHandler,
    dispatchGraphAction,
    setPendingGraphAction,
  }

  return <InvestigateContext.Provider value={value}>{children}</InvestigateContext.Provider>
}

export function useInvestigateContext(): InvestigateContextValue {
  const value = useContext(InvestigateContext)
  if (!value) throw new Error('useInvestigateContext must be used inside InvestigateLayout')
  return value
}

export function useInvestigateContextOptional(): InvestigateContextValue | null {
  return useContext(InvestigateContext)
}
